import {
  animate,
  AnimationBuilder,
  AnimationPlayer,
  style,
} from '@angular/animations';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewEncapsulation,
} from '@angular/core';
import { NkSidebarService } from './sidebar.service';
import { MatSidenavModule } from '@angular/material/sidenav';

interface IConfig {
  type: 'left' | 'right';
  large: boolean;
  open: boolean;
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports :[MatSidenavModule ]
})
export class NkSidebarComponent implements OnInit, OnDestroy {
  #config: IConfig = {
    type: 'right',
    large: false,
    open: true,
  };

  @Input() name!: string;

  @Input()
  set config(value: IConfig) {
    if (!value) return;
    this.updateSideBar(this.#config, value);
    this.#config = value;
  }
  get config() {
    return this.#config;
  }

  @HostBinding('class.open') opened = false;
  @HostBinding('class.locked-open') isLockedOpen = false;

  private _folded = false;
  @HostBinding('class.folded')
  set folded(value: boolean) {
    if (!this.opened) {
      this._folded = false;
      return;
    }
    this._folded = value;

    let sibling: HTMLElement | null = null;
    let styleRule = '';
    const styleValue = '64px';

    if (this.config.type === 'left') {
      sibling = this.elementRef.nativeElement.nextElementSibling;
      styleRule = 'marginLeft';
    } else {
      sibling = this.elementRef.nativeElement.previousElementSibling;
      styleRule = 'marginRight';
    }

    if (!sibling) return;

    if (value) {
      this.renderer.setStyle(sibling, styleRule, styleValue);
    } else {
      this.renderer.removeStyle(sibling, styleRule);
    }
  }
  get folded(): boolean {
    return this._folded;
  }

  @HostBinding('class.unfolded') unfolded = false;

  @Output() backdropClicked = new EventEmitter<void>();

  private _backdrop: HTMLElement | any = null;
  private _player!: AnimationPlayer;

  // Named handler for removing event listener on destroy
  private _backdropClickHandler = () => this.backdropClicked.emit();

  constructor(
    private animationBuilder: AnimationBuilder,
    private elementRef: ElementRef,
    private nkSidebarService: NkSidebarService,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.nkSidebarService.register(this.name, this);
    this.setupAlignment(this.config.type);
    this.setupLockedOpen(this.config);
  }

  ngOnDestroy(): void {
    if (this.folded) {
      this.folded = false;
    }
    this.nkSidebarService.unregister(this.name);
    // Remove backdrop click listener if it exists
    if (this._backdrop) {
      this._backdrop.removeEventListener('click', this._backdropClickHandler);
    }
  }

  private updateSideBar(oldConfig: IConfig, newConfig: IConfig) {
    if (oldConfig.type !== newConfig.type) {
      this.removeAlignment(oldConfig.type);
      this.setupAlignment(newConfig.type);
    }
    this.setupLockedOpen(newConfig);
  }

  private setupAlignment(align: string): void {
    this.renderer.addClass(this.elementRef.nativeElement, align + '-aligned');
  }

  private removeAlignment(align: string): void {
    this.renderer.removeClass(this.elementRef.nativeElement, align + '-aligned');
  }

  private setupLockedOpen(config: IConfig): void {
    if (config.large) {
      this.hideBackdrop();
      this.isLockedOpen = true;
      this.folded = !config.open; // folded = true if open=false
      this.opened = true; // sidebar is always opened in locked mode
    } else {
      this.isLockedOpen = false;
      this.folded = false;
      if (config.open) {
        this.opened = true;
        this.showBackdrop();
      } else {
        this.opened = false;
        this.hideBackdrop();
      }
    }
  }

  @HostListener('mouseenter')
  onMouseEnter() {
    if (this.folded) {
      this.unfolded = true;
    }
  }

  @HostListener('mouseleave')
  onMouseLeave() {
    if (this.folded) {
      this.unfolded = false;
    }
  }

  private showBackdrop(): void {
    if (this._backdrop) {
      return;
    }

    this._backdrop = this.renderer.createElement('div');
    this._backdrop.classList.add('nk-sidebar-overlay');
    this.renderer.appendChild(
      this.elementRef.nativeElement.parentElement,
      this._backdrop
    );

    this._player = this.animationBuilder
      .build([animate('300ms ease', style({ opacity: 1 }))])
      .create(this._backdrop);

    this._player.play();

    this._backdrop.addEventListener('click', this._backdropClickHandler);
  }

  private hideBackdrop(): void {
    if (!this._backdrop) {
      return;
    }

    this._player = this.animationBuilder
      .build([animate('300ms ease', style({ opacity: 0 }))])
      .create(this._backdrop);

    this._player.play();

    this._player.onDone(() => {
      if (this._backdrop && this._backdrop.parentNode) {
        this._backdrop.parentNode.removeChild(this._backdrop);
        this._backdrop.removeEventListener('click', this._backdropClickHandler);
        this._backdrop = null;
      }
    });
  }
}
